import { CheckCircle2, Clock, Loader2, RefreshCw, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

interface HarnessJob {
  id: string;
  status: "queued" | "claimed" | "running" | "succeeded" | "failed" | "cancelled";
  worker_id: string | null;
  prompt_preview: string;
  error_message: string | null;
  session_id: number | null;
  created_at: string | null;
  claimed_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  elapsed_seconds: number | null;
  timeout_seconds: number;
}

// Poll faster than the workers panel so an in-flight job's status/elapsed updates
// feel live while you watch it work.
const REFRESH_MS = 5_000;
const ACTIVE_STATUSES = new Set(["queued", "claimed", "running"]);

function StatusBadge({ status }: Readonly<{ status: HarnessJob["status"] }>) {
  const map: Record<string, { icon: typeof Clock; cls: string; label: string; spin?: boolean }> = {
    queued: { icon: Clock, cls: "text-content-tertiary", label: "Queued" },
    claimed: { icon: Loader2, cls: "text-amber-400", label: "Starting", spin: true },
    running: { icon: Loader2, cls: "text-blue-400", label: "Running", spin: true },
    succeeded: { icon: CheckCircle2, cls: "text-green-400", label: "Succeeded" },
    failed: { icon: XCircle, cls: "text-red-400", label: "Failed" },
    cancelled: { icon: XCircle, cls: "text-content-tertiary", label: "Cancelled" },
  };
  const s = map[status] ?? map.queued;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${s.cls}`}>
      <Icon className={`h-3.5 w-3.5 ${s.spin ? "animate-spin" : ""}`} />
      {s.label}
    </span>
  );
}

function formatElapsed(seconds: number | null): string {
  if (seconds == null) return "";
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
}

function JobRow({ job }: Readonly<{ job: HarnessJob }>) {
  const active = ACTIVE_STATUSES.has(job.status);
  return (
    <div className="p-3 bg-surface-secondary border border-border-secondary rounded-lg">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-content-primary truncate" title={job.prompt_preview}>
            {job.prompt_preview || "(no prompt)"}
          </p>
          <div className="flex items-center gap-3 mt-1 text-xs text-content-tertiary">
            <code className="font-mono truncate">{job.id.slice(0, 8)}</code>
            {job.elapsed_seconds != null && (
              <span>
                {active ? "Elapsed" : "Took"} {formatElapsed(job.elapsed_seconds)}
              </span>
            )}
            {job.worker_id && <span className="truncate">on {job.worker_id.slice(0, 16)}</span>}
          </div>
          {job.status === "failed" && job.error_message && (
            <p className="mt-1 text-xs text-red-400 line-clamp-2" title={job.error_message}>
              {job.error_message}
            </p>
          )}
        </div>
        <StatusBadge status={job.status} />
      </div>
    </div>
  );
}

export function HarnessJobsManager() {
  const [jobs, setJobs] = useState<HarnessJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchJobs = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const data = await api.get<{ data: HarnessJob[] }>("/harness/jobs?limit=25");
      setJobs(data.data);
      setError(null);
    } catch {
      setError("Failed to load harness jobs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const id = setInterval(() => fetchJobs(), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchJobs]);

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-medium text-content-primary">Recent work</h3>
          <p className="text-sm text-content-tertiary">
            Jobs delegated to RALLY machines. Running jobs update automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchJobs(true)}
          className="flex items-center gap-2 px-3 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-sm font-medium text-content-primary transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <JobList loading={loading} jobs={jobs} />
    </div>
  );
}

function JobList({ loading, jobs }: Readonly<{ loading: boolean; jobs: HarnessJob[] }>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-content-tertiary" />
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <p className="text-sm text-content-tertiary py-4">
        No harness jobs yet. When an agent delegates work to a RALLY machine, it will appear here with live status.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {jobs.map((j) => (
        <JobRow key={j.id} job={j} />
      ))}
    </div>
  );
}
